import { createServerFn } from "@tanstack/react-start";
import { and, asc, count, desc, eq } from "drizzle-orm";
import { ensureFreeTierProvider } from "~/lib/ai/free-tier";
import { authMiddleware } from "~/lib/auth/middleware";
import { getDb } from "~/lib/db";
import {
  aiProviderConnection,
  chatMessage,
  chatSession,
  promptTemplate,
  userPreferences,
} from "~/lib/db/schema";

interface GetTemplateInput {
  id: string;
}

export const $getPromptTemplates = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { user } = context;
    const db = getDb();

    const templates = await db.query.promptTemplate.findMany({
      where: eq(promptTemplate.userId, user.id),
      orderBy: [
        desc(promptTemplate.isFavorite),
        desc(promptTemplate.usageCount),
        asc(promptTemplate.name),
      ],
      with: {
        defaultProvider: {
          columns: {
            displayName: true,
          },
        },
      },
    });

    return templates;
  });

interface GetRecentChatsInput {
  limit?: number;
  offset?: number;
}

export interface RecentChatWithPreview {
  id: string;
  title: string;
  model: string;
  providerId: string | null;
  promptTemplateId: string | null;
  isPinned: boolean;
  createdAt: Date;
  updatedAt: Date;
  messageCount: number;
  firstMessagePreview: string | null;
}

export const $getRecentChats = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .inputValidator((input: GetRecentChatsInput = {}) => input)
  .handler(async ({ context, data }) => {
    const { user } = context;
    const db = getDb();
    const limit = data.limit || 15;
    const offset = data.offset || 0;

    const chats = await db.query.chatSession.findMany({
      where: eq(chatSession.userId, user.id),
      orderBy: (table) => [desc(table.isPinned), desc(table.updatedAt)],
      limit,
      offset,
      with: {
        messages: {
          limit: 1,
          orderBy: asc(chatMessage.createdAt),
          columns: {
            content: true,
          },
        },
      },
    });

    return chats.map((chat) => ({
      id: chat.id,
      title: chat.title,
      model: chat.model,
      providerId: chat.providerId,
      promptTemplateId: chat.promptTemplateId,
      isPinned: chat.isPinned,
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
      messageCount: chat.messageCount,
      firstMessagePreview: chat.messages[0]?.content ?? null,
    }));
  });

export const $getPromptTemplate = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .inputValidator((input: GetTemplateInput) => input)
  .handler(async ({ context, data }) => {
    const { user } = context;
    const db = getDb();

    const template = await db.query.promptTemplate.findFirst({
      where: eq(promptTemplate.id, data.id),
      with: {
        defaultProvider: {
          columns: {
            id: true,
            displayName: true,
          },
        },
      },
    });

    if (!template || template.userId !== user.id) {
      throw new Error("Template not found");
    }

    return template;
  });

export const $getAiProviders = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { user } = context;
    const db = getDb();

    await ensureFreeTierProvider(user.id);

    const providers = await db.query.aiProviderConnection.findMany({
      where: eq(aiProviderConnection.userId, user.id),
      columns: {
        id: true,
        displayName: true,
        provider: true,
        isDefault: true,
      },
      orderBy: [
        desc(aiProviderConnection.isDefault),
        asc(aiProviderConnection.displayName),
      ],
    });

    return providers;
  });

// Get full AI provider connections (for settings page)
export const $getAiProviderConnections = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { user } = context;
    const db = getDb();

    await ensureFreeTierProvider(user.id);

    const providers = await db.query.aiProviderConnection.findMany({
      where: eq(aiProviderConnection.userId, user.id),
      orderBy: [
        desc(aiProviderConnection.isDefault),
        asc(aiProviderConnection.displayName),
      ],
    });

    return providers;
  });

// Get dashboard stats
export const $getDashboardStats = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { user } = context;
    const db = getDb();

    const [totalChatsResult] = await db
      .select({ count: count() })
      .from(chatSession)
      .where(eq(chatSession.userId, user.id));

    const [totalTemplatesResult] = await db
      .select({ count: count() })
      .from(promptTemplate)
      .where(eq(promptTemplate.userId, user.id));

    const [totalMessagesResult] = await db
      .select({ count: count() })
      .from(chatMessage)
      .innerJoin(chatSession, eq(chatMessage.chatSessionId, chatSession.id))
      .where(eq(chatSession.userId, user.id));

    const [activeProvidersResult] = await db
      .select({ count: count() })
      .from(aiProviderConnection)
      .where(
        and(
          eq(aiProviderConnection.userId, user.id),
          eq(aiProviderConnection.isActive, true),
        ),
      );

    return {
      totalChats: totalChatsResult.count,
      totalTemplates: totalTemplatesResult.count,
      totalMessages: totalMessagesResult.count,
      activeProviders: activeProvidersResult.count,
    };
  });

// Get user preferences
export const $getUserPreferences = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { user } = context;
    const db = getDb();

    let preferences = await db.query.userPreferences.findFirst({
      where: eq(userPreferences.userId, user.id),
    });

    // Create default preferences if they don't exist
    if (!preferences) {
      [preferences] = await db
        .insert(userPreferences)
        .values({
          userId: user.id,
        })
        .returning();
    }

    return preferences;
  });
